import { and, eq, inArray, isNull } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@custodia/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync's **view** can
 * match **ZERO rows and return "success"**. This is not a hypothesis: it was measured in
 * `space-academy-app` (same canon, `activity` table from the local stack, course
 * `06a131d0`) — the course is created, 1.03 s later the unit is created and **between
 * them there is NO `course/updated` op**: the title the author had just
 * written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue drains, PowerSync **rebuilds the
 * local view from the latest checkpoint**. If the checkpoint does not yet contain the
 * newly created row, the row is **ABSENT at that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**. The window is
 * not "X ms passed": it is "the queue drained in between". It is not detected afterward by
 * looking at timings, and a case that landed correctly proves nothing.
 *
 * Why the absence of an error proves nothing here: the server write path
 * intentionally **always returns 2xx** (a 4xx would block the client queue
 * forever). In CUSTODIA this is more serious than in any other domain:
 * a profile tag or classroom assignment the GUARDIAN believes they
 * saved but that is silently lost is a security failure, not just a data failure —
 * just as a disappearing rule would be (those travel through
 * `POST /policy`, see `supervision/mutations.ts`, so this particular window
 * does not touch them, but the criterion is the same).
 *
 * `patchRowVerified`/`patchRowsVerified` contract: write, **READ BACK**,
 * compare. If the value did not land, retry with backoff; once exhausted and it still
 * has not landed, **THROW** — the caller reports it instead of leaving the
 * guardian looking at a field that represents nothing saved.
 *
 * No mutation in this repository opens `db.transaction(...)` (the only candidate,
 * the profile/classroom lifecycle, consists of separate steps), so we do not carry
 * `waitForRowPresent`. The signal that would reopen this: a mutation that starts wrapping
 * an INSERT+UPDATE in a transaction.
 *
 * ── What this module does NOT cover (deliberately) ───────────────────────────
 * Hard `DELETE`s (`deleteSupervisedProfileMutation`, `deleteAulaMutation`)
 * are not verified: their natural check would be "the row is no longer there", and
 * "absent" is precisely the ambiguous state of this window — you cannot distinguish
 * "it was deleted" from "it had not arrived yet". Verifying them would require another signal (the
 * CRUD queue itself), not a re-read. The signal that would reopen this: a deletion
 * that is actually lost (a row reappearing after deletion).
 *
 * `user` DOES pass through here, unlike `comandero-app`/`folio-office-app`
 * (where the server creates the row at registration). The reason is NO LONGER that
 * `ThemeProvider` seeds locally for any `uid` — that was closed on
 * 2026-08-13 (guard `uid !== VISITOR_ID` in
 * `apps/web/src/providers/theme-provider.tsx`; see the reasoning next to the
 * guard and the measurement in
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`).
 * It remains true for a reason INDEPENDENT of that seeding:
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as the local
 *     SQLite is opened, not when the initial sync finishes.
 *   - `userId()` becomes active as soon as `signUpActor`/`signInActor` receive the
 *     server session (`packages/shared/src/auth/controllers.ts`) — an independent REST call from the PowerSync stream.
 *
 * Between the time `db`+`userId()` are ready and the initial checkpoint brings the
 * `user` row the server unconditionally created at registration
 * (`packages/core-rust/src/db/user.rs:101,350`), there is a window in which a normal
 * `UPDATE` (change language, theme…) hits a row the local view does not
 * have yet — it matches ZERO rows and returns "success" silently.
 * That window does not depend on anyone seeding a local row: it is the view
 * synchronizing, nothing more. The `ThemeProvider` guard closed a factor that
 * WORSENED it for real accounts (a local ghost row competing with the
 * real one on the way); it neither opened nor closed it. **Do not use the guard as a reason
 * to exclude `user` from here** — if someday the window above (`db`/`userId()` before the checkpoint) is
 * shown not to exist, that would be the signal, not that `ThemeProvider` stopped
 * seeding ghosts.
 *
 * ── `scope`: tenant isolation, not just correctness ──────────────────────
 * `supervised_profile`/`aula` sync (`infra/sync_streams.yaml`,
 * stream `org_supervision`) with `WHERE organization_id IN my_org_ids`, ALL
 * organizations the user is a `member` of — a guardian who is also
 * a teacher at two schools has both synced simultaneously in SQLite. The
 * profile id also travels in the URL (`/supervised/$profileId`, an addressable route).
 * `id` is already a unique UUID (not needed for CORRECTNESS), but without
 * `scope` an `UPDATE … WHERE id = ?` launched with an id from another
 * organization would land just the same if that id were synced locally.
 * `scope` (applied to the UPDATE and the re-read) treats a row that does not meet
 * the scope just like an absent row: the write is never confirmed and
 * `patchRowVerified` ends by throwing `LocalWriteLostError` — the same mechanism
 * as the checkpoint window, for the right reason (it is not yours).
 * `value: null` in a `ScopeCondition` compiles to `IS NULL` (personal profiles,
 * `organization_id` null), not `= NULL` (which never matches in SQL).
 */

/**
 * Retry backoff. Sum ≈ 6.05 s: more than enough for a checkpoint round trip
 * without leaving a quick interaction — enabling location,
 * renaming a profile — hanging forever if the real row does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — tenant isolation in depth.*/
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds.*/
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO satisfy, in addition to `id` —
   * typically `organization_id`. They apply to both the UPDATE and the
   * re-read: a row that does not meet the scope is treated just like an absent row
   * (see the security note above).
   */
  scope?: readonly ScopeCondition[];
}

/** Error from a local-first write that did not land in the view.*/
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** `eq`, or `isNull` if the scope value is `null` (personal profiles).*/
function scopeCondition(column: SQLiteColumn, value: unknown) {
  return value === null ? isNull(column) : eq(column, value);
}

/** Builds `eq(id) AND scope1 AND …` — or just `eq(id)` without scope.*/
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => scopeCondition(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not meet the scope — the same result as "row absent",
 * intentionally (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key (+ scope), so the row is a flat record of
  // columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** Same as `readRow`, but for several rows by `id IN (...)`.*/
async function readRows(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
): Promise<Record<string, unknown>[]> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await db.select().from(table).where(inArray(idColumn, ids))) as unknown as Record<
    string,
    unknown
  >[];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite.*/
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verified**: write, re-read, compare the patch fields,
 * and retry while they have not landed. Throws `LocalWriteLostError`
 * if they still have not landed after the backoff is exhausted.
 *
 * `updated_at` is set by this function on every attempt (and is not compared: it changes
 * each pass); `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Same as `patchRowVerified`, adapted to an `UPDATE … WHERE id IN (...)`
 * over SEVERAL rows by primary key (cohort of profiles unassigned when
 * deleting a classroom). It lands only when ALL rows in `ids` show the
 * patch — if even one is outside the view, the entire batch is retried.
 */
export async function patchRowsVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const idKey = idColumn.name;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(inArray(idColumn, ids));

    const rows = await readRows(db, table, idColumn, ids);
    const byId = new Map(rows.map((row) => [row[idKey], row]));
    const landed = ids.every((id) => {
      const row = byId.get(id);
      return row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value));
    });
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(ids.join(", "), Object.keys(patch));
    }
    await sleep(wait);
  }
}
